import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import toast from 'react-hot-toast';
import {
  WebSocketClient,
  WebSocketMessage,
  WebSocketEventType,
  MessageHandler,
  getWebSocketClient,
} from '../lib/websocket';
import { useAuth } from './AuthContext';

interface WebSocketContextValue {
  isConnected: boolean;
  connectionState: 'connecting' | 'open' | 'closing' | 'closed';
  subscribeToFarm: (farmId: string) => void;
  unsubscribeFromFarm: (farmId: string) => void;
  on: (eventType: WebSocketEventType, handler: MessageHandler) => () => void;
  onAny: (handler: MessageHandler) => () => void;
  lastMessage: WebSocketMessage | null;
  notifications: WebSocketMessage[];
  clearNotifications: () => void;
}

const WebSocketContext = createContext<WebSocketContextValue | undefined>(undefined);

interface WebSocketProviderProps {
  children: ReactNode;
}

export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const { user, isAuthenticated } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState<'connecting' | 'open' | 'closing' | 'closed'>('closed');
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const [notifications, setNotifications] = useState<WebSocketMessage[]>([]);
  const [client, setClient] = useState<WebSocketClient | null>(null);

  useEffect(() => {
    const wsClient = getWebSocketClient({
      onConnect: () => {
        setIsConnected(true);
        setConnectionState('open');
      },
      onDisconnect: () => {
        setIsConnected(false);
        setConnectionState('closed');
      },
      onError: () => {
        setConnectionState('closed');
      },
    });

    setClient(wsClient);

    // Connect when user is authenticated
    if (isAuthenticated) {
      wsClient.connect();

      // Authenticate after connection
      if (user?.id) {
        setTimeout(() => {
          wsClient.authenticate(user.id);
        }, 500);
      }
    }

    return () => {
      wsClient.disconnect();
    };
  }, [isAuthenticated, user?.id]);

  // Handle global notifications
  useEffect(() => {
    if (!client) return;

    const unsubscribe = client.onAny((message) => {
      setLastMessage(message);

      // Handle notifications
      switch (message.type) {
        case 'alert:new':
          const alertPayload = message.payload as {
            severity: string;
            message: string;
          };
          setNotifications((prev) => [message, ...prev].slice(0, 50));

          // Show toast for new alerts
          if (alertPayload.severity === 'critical') {
            toast.error(alertPayload.message, { duration: 6000 });
          } else if (alertPayload.severity === 'warning') {
            toast(alertPayload.message, {
              icon: '⚠️',
              duration: 5000,
            });
          } else {
            toast(alertPayload.message, { duration: 4000 });
          }
          break;

        case 'analysis:complete':
          const analysisPayload = message.payload as { jobId: string };
          toast.success(`Analysis job ${analysisPayload.jobId.slice(0, 8)} completed!`);
          setNotifications((prev) => [message, ...prev].slice(0, 50));
          break;

        case 'analysis:error':
          const errorPayload = message.payload as { error: string };
          toast.error(`Analysis failed: ${errorPayload.error}`);
          break;

        case 'indices:updated':
          setNotifications((prev) => [message, ...prev].slice(0, 50));
          break;
      }
    });

    return unsubscribe;
  }, [client]);

  const subscribeToFarm = useCallback(
    (farmId: string) => {
      client?.subscribeToFarm(farmId);
    },
    [client]
  );

  const unsubscribeFromFarm = useCallback(
    (farmId: string) => {
      client?.unsubscribeFromFarm(farmId);
    },
    [client]
  );

  const on = useCallback(
    (eventType: WebSocketEventType, handler: MessageHandler) => {
      return client?.on(eventType, handler) || (() => {});
    },
    [client]
  );

  const onAny = useCallback(
    (handler: MessageHandler) => {
      return client?.onAny(handler) || (() => {});
    },
    [client]
  );

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const value: WebSocketContextValue = {
    isConnected,
    connectionState,
    subscribeToFarm,
    unsubscribeFromFarm,
    on,
    onAny,
    lastMessage,
    notifications,
    clearNotifications,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket(): WebSocketContextValue {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}

export default WebSocketContext;
